// src/views/pagamentos/LotePagamentoModal.jsx
// Modal único para baixar várias parcelas em uma operação. Mostra resumo
// (qtd, valor total, saldo antes/depois) antes de confirmar.
import React, { useState, useEffect, useMemo } from "react";
import contasBancariasService from "../../services/contasBancariasService";

const fmtMoeda = v => parseFloat(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtDt    = dt => { if(!dt) return "—"; try { return new Date(String(dt).split("T")[0]+"T12:00:00").toLocaleDateString("pt-BR"); } catch { return dt; } };

export default function LotePagamentoModal({ show, onClose, onConfirmar, parcelas=[], saving=false, idCondominio, modo="lote" }) {
  // modo: "lote" (Pagar agora) | "agendar" (Agendar para data futura)
  const isAgendar = modo === "agendar";

  const [contasBancarias, setContasBancarias] = useState([]);
  const [form, setForm] = useState({
    idContaBancaria: "",
    tpPagamento: "PIX",
    dtPagamento: new Date().toISOString().split("T")[0],
    dtPagamentoProgramado: "",
    pagoPor: "",
    observacao: "",
  });

  useEffect(() => {
    if (!idCondominio || !show) return;
    contasBancariasService.listar({ idCondominio, situacao:"ativo" })
      .then(r => setContasBancarias(r.data||[]))
      .catch(()=>{});
  }, [idCondominio, show]);

  // Reseta o form ao reabrir o modal.
  useEffect(() => {
    if (!show) return;
    setForm(f => ({
      ...f,
      idContaBancaria: "",
      dtPagamento: new Date().toISOString().split("T")[0],
      dtPagamentoProgramado: new Date(Date.now()+86400000).toISOString().split("T")[0],
      pagoPor: "",
      observacao: "",
    }));
  }, [show]);

  const totais = useMemo(() => {
    const total = parcelas.reduce((a, p) => a + parseFloat(p.vlrParcela||0), 0);
    const fornecedores = [...new Set(parcelas.map(p => p.nmFornecedor).filter(Boolean))];
    return { qtd: parcelas.length, total, fornecedores };
  }, [parcelas]);

  const contaSel = contasBancarias.find(c => String(c.id) === String(form.idContaBancaria));
  const saldoAtual = parseFloat(contaSel?.saldoAtual||0);
  const saldoApos  = saldoAtual - totais.total;
  const saldoInsuficiente = !!contaSel && !isAgendar && saldoApos < 0;

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleConfirmar = () => {
    if (!form.idContaBancaria) return alert("Selecione a conta bancária de débito.");
    if (isAgendar) {
      if (!form.dtPagamentoProgramado) return alert("Informe a data programada.");
      onConfirmar({
        ids: parcelas.map(p => p.id),
        idContaBancaria: form.idContaBancaria,
        tpPagamento: form.tpPagamento,
        dtPagamentoProgramado: form.dtPagamentoProgramado,
        observacao: form.observacao,
      });
    } else {
      if (!form.dtPagamento) return alert("Informe a data do pagamento.");
      if (saldoInsuficiente) {
        if (!window.confirm(`Saldo ficará negativo em ${fmtMoeda(Math.abs(saldoApos))}. Continuar mesmo assim?`)) return;
      }
      onConfirmar({
        ids: parcelas.map(p => p.id),
        idContaBancaria: form.idContaBancaria,
        tpPagamento: form.tpPagamento,
        dtPagamento: form.dtPagamento,
        pagoPor: form.pagoPor,
        observacao: form.observacao,
      });
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header" style={{background: isAgendar ? "#0891B2" : "#166534"}}>
            <div className="d-flex align-items-center gap-3">
              <div style={{width:38,height:38,background:"rgba(255,255,255,.15)",borderRadius:8,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <i className={`bi ${isAgendar?"bi-calendar-plus":"bi-cash-coin"}`} style={{fontSize:18,color:"#fff"}}/>
              </div>
              <div>
                <h5 className="modal-title mb-0">
                  {isAgendar ? "Agendar Pagamentos em Lote" : "Pagar em Lote"}
                </h5>
                <small style={{color:"rgba(255,255,255,.7)",fontSize:12}}>
                  {totais.qtd} parcela(s) selecionada(s) — Total: {fmtMoeda(totais.total)}
                </small>
              </div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose}/>
          </div>

          <div className="modal-body">
            {/* Resumo */}
            <div style={{background: isAgendar?"#E0F2FE":"#F0FDF4",
              border:`1px solid ${isAgendar?"#7DD3FC":"#BBF7D0"}`,
              borderRadius:8, padding:"12px 16px", marginBottom:16}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,fontSize:13}}>
                <div>
                  <div style={{fontSize:11,color:"var(--gray-500)",textTransform:"uppercase",fontWeight:600}}>Parcelas</div>
                  <div style={{fontSize:22,fontWeight:700,color: isAgendar?"#075985":"#166534"}}>{totais.qtd}</div>
                </div>
                <div>
                  <div style={{fontSize:11,color:"var(--gray-500)",textTransform:"uppercase",fontWeight:600}}>Fornecedores</div>
                  <div style={{fontSize:22,fontWeight:700,color: isAgendar?"#075985":"#166534"}}>{totais.fornecedores.length}</div>
                </div>
                <div>
                  <div style={{fontSize:11,color:"var(--gray-500)",textTransform:"uppercase",fontWeight:600}}>Total a {isAgendar?"agendar":"debitar"}</div>
                  <div style={{fontSize:22,fontWeight:800,color: isAgendar?"#075985":"#166534"}}>{fmtMoeda(totais.total)}</div>
                </div>
              </div>
            </div>

            {/* Lista das parcelas */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:"var(--gray-700)",marginBottom:8}}>
                <i className="bi bi-list-check me-2"/>Parcelas incluídas no lote
              </div>
              <div style={{maxHeight:160, overflowY:"auto", border:"1px solid var(--gray-200)", borderRadius:8}}>
                <table className="table table-sm mb-0" style={{fontSize:12}}>
                  <thead style={{background:"var(--gray-50)",position:"sticky",top:0}}>
                    <tr>
                      <th style={{padding:"6px 10px"}}>Fornecedor</th>
                      <th style={{padding:"6px 10px"}}>Descrição</th>
                      <th style={{padding:"6px 10px"}}>Venc.</th>
                      <th style={{padding:"6px 10px",textAlign:"right"}}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parcelas.map(p => (
                      <tr key={p.id}>
                        <td style={{padding:"6px 10px",fontWeight:600}}>{p.nmFornecedor||"—"}</td>
                        <td style={{padding:"6px 10px",color:"var(--gray-600)",maxWidth:240,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.descricao}</td>
                        <td style={{padding:"6px 10px"}}>{fmtDt(p.dtVencimento)}</td>
                        <td style={{padding:"6px 10px",textAlign:"right",fontWeight:600}}>{fmtMoeda(p.vlrParcela)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Formulário do pagamento */}
            <div className="form-section">
              <div className="form-section-title">
                <i className={`bi ${isAgendar?"bi-calendar-event":"bi-credit-card"}`}/>
                {isAgendar ? " Dados do Agendamento" : " Dados do Pagamento"}
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Conta Bancária (débito) <span className="required-mark">*</span></label>
                  <select className="form-select" value={form.idContaBancaria}
                    onChange={e=>set("idContaBancaria",e.target.value)}>
                    <option value="">— Selecione —</option>
                    {contasBancarias.map(c => (
                      <option key={c.id} value={c.id}>{c.nmConta} — {fmtMoeda(c.saldoAtual)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Forma de Pagamento</label>
                  <select className="form-select" value={form.tpPagamento}
                    onChange={e=>set("tpPagamento",e.target.value)}>
                    {["PIX","Transferencia","TED","DOC","Cheque","Dinheiro","DebitoAutomatico","Outro"].map(t => (
                      <option key={t} value={t}>{t.replace(/([A-Z])/g," $1").trim()}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    {isAgendar ? "Data Programada" : "Data do Pagamento"} <span className="required-mark">*</span>
                  </label>
                  <input type="date" className="form-control"
                    value={isAgendar ? form.dtPagamentoProgramado : form.dtPagamento}
                    onChange={e=>set(isAgendar?"dtPagamentoProgramado":"dtPagamento", e.target.value)}
                    min={isAgendar ? new Date().toISOString().split("T")[0] : undefined}/>
                </div>

                {!isAgendar && (
                  <div className="col-md-6">
                    <label className="form-label">Pago por</label>
                    <input className="form-control" value={form.pagoPor}
                      onChange={e=>set("pagoPor",e.target.value)} placeholder="Nome de quem autorizou"/>
                  </div>
                )}

                <div className="col-12">
                  <label className="form-label">Observação (será aplicada a todas as parcelas)</label>
                  <textarea className="form-control" rows={2} value={form.observacao}
                    onChange={e=>set("observacao",e.target.value)}/>
                </div>
              </div>

              {/* Aviso de saldo */}
              {!isAgendar && contaSel && (
                <div style={{
                  marginTop:14,
                  background: saldoInsuficiente ? "#FEE2E2" : "#F0FDF4",
                  border: `1px solid ${saldoInsuficiente?"#FECACA":"#BBF7D0"}`,
                  borderRadius:8, padding:"10px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
                    <span style={{color:"var(--gray-600)"}}>Saldo atual da conta:</span>
                    <strong style={{color:"var(--gray-800)"}}>{fmtMoeda(saldoAtual)}</strong>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginTop:4}}>
                    <span style={{color:"var(--gray-600)"}}>Total a debitar:</span>
                    <strong style={{color:"#991B1B"}}>− {fmtMoeda(totais.total)}</strong>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:14,marginTop:6,
                    paddingTop:6,borderTop:`1px dashed ${saldoInsuficiente?"#FECACA":"#BBF7D0"}`}}>
                    <span style={{color:"var(--gray-700)",fontWeight:600}}>Saldo após pagamento:</span>
                    <strong style={{color: saldoInsuficiente?"#991B1B":"#166534"}}>{fmtMoeda(saldoApos)}</strong>
                  </div>
                  {saldoInsuficiente && (
                    <div style={{fontSize:12,color:"#991B1B",marginTop:6}}>
                      <i className="bi bi-exclamation-triangle-fill me-1"/>
                      O saldo ficará negativo. Confirme se há cheque especial ou ajuste a seleção.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              <i className="bi bi-x-circle"/> Cancelar
            </button>
            <button className={`btn ${isAgendar?"btn-info text-white":"btn-success"}`}
              onClick={handleConfirmar} disabled={saving}>
              {saving
                ? <><span className="spinner-border spinner-border-sm me-2"/>Processando...</>
                : <><i className={`bi ${isAgendar?"bi-calendar-check":"bi-check-circle"}`}/>
                    {isAgendar ? " Agendar Pagamentos" : ` Confirmar ${totais.qtd} Pagamento(s)`}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
